// The Summaries list's filters, as the address carries them (`RPM-103`, `CR-45` R5, `J-602`).
//
// "Filters as the worklist has them, including a due-date range and a sent-date range." Every one is
// kept in the address, as the worklist's are, so a reload, a bookmark and Back to summaries return to
// the same list: a building's mirror code, words from closed lists, calendar dates, how many pages
// were shown, and the summary last opened. None of them is a name (`.claude/rules/phi.md` rule 2).
//
// EVERY FIELD STARTS EMPTY AND A BAD ONE IS DROPPED. An address edited by hand reads as that filter
// not set, never as an error page (`.catch(undefined)`).
//
// PURE, IN ITS OWN FILE: a helper exported beside a component fails `react-refresh` at
// `--max-warnings 0`, and the route and the list both read these.
import { z } from "zod";
import type { paths } from "../../api/schema";

type ListQuery = NonNullable<paths["/api/rpm/v1/summaries"]["get"]["parameters"]["query"]>;

/** The kinds the Kind control offers. */
export const KIND_CHOICES = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
] as const satisfies readonly { value: NonNullable<ListQuery["kind"]>; label: string }[];

/** The states the State control offers. */
export const STATE_CHOICES = [
  { value: "DUE", label: "Due" },
  { value: "SENT", label: "Sent" },
] as const satisfies readonly { value: NonNullable<ListQuery["state"]>; label: string }[];

const oneOf = <T extends readonly { value: string }[]>(choices: T) =>
  z
    .enum(choices.map((c) => c.value) as [T[number]["value"], ...T[number]["value"][]])
    .optional()
    .catch(undefined);

const day = z.iso.date().optional().catch(undefined);

/** The Summaries address. Ids, words from closed lists, dates and a page count only; never a name. */
export const summariesSearchSchema = z.object({
  facilityId: z.string().min(1).max(20).optional().catch(undefined),
  kind: oneOf(KIND_CHOICES),
  state: oneOf(STATE_CHOICES),
  dueFrom: day,
  dueTo: day,
  sentFrom: day,
  sentTo: day,
  sentBy: z.literal("me").optional().catch(undefined),
  /** How many pages Show more had shown, so Back to summaries shows as many again. */
  pages: z.number().int().min(2).max(100).optional().catch(undefined),
  /** The summary last opened from here, so Back to summaries returns focus to its row. */
  opened: z.uuid().optional().catch(undefined),
});
export type SummariesSearch = z.infer<typeof summariesSearchSchema>;

/** The filters alone: the address without the page count and the row last opened. */
export type SummaryFilters = Omit<SummariesSearch, "pages" | "opened">;

/** A range entered backwards is read the right way round rather than refused. */
function ordered(from: string | undefined, to: string | undefined) {
  return from !== undefined && to !== undefined && from > to ? [to, from] : [from, to];
}

/** The read the filters ask for, without the page. */
export function summariesQueryOf(f: SummaryFilters): Omit<ListQuery, "limit" | "cursor"> {
  const [dueFrom, dueTo] = ordered(f.dueFrom, f.dueTo);
  const [sentFrom, sentTo] = ordered(f.sentFrom, f.sentTo);
  return {
    ...(f.facilityId === undefined ? {} : { facilityId: f.facilityId }),
    ...(f.kind === undefined ? {} : { kind: f.kind }),
    ...(f.state === undefined ? {} : { state: f.state }),
    ...(dueFrom === undefined ? {} : { dueFrom }),
    ...(dueTo === undefined ? {} : { dueTo }),
    ...(sentFrom === undefined ? {} : { sentFrom }),
    ...(sentTo === undefined ? {} : { sentTo }),
    ...(f.sentBy === undefined ? {} : { sentBy: f.sentBy }),
  };
}

/** Whether any filter but the building is set: Clear every filter is offered then. */
export function anyFilterSet(f: SummaryFilters): boolean {
  return Object.values(summariesQueryOf(f)).some((v) => v !== undefined);
}

/**
 * A FILTER CHANGE IS MERGED INTO THE ADDRESS AS IT IS WHEN WRITTEN, as the worklist's is (`J-228`):
 * each control builds its change from the filters it was drawn with, so only the keys a change alters
 * are applied. The page count and the row last opened are dropped: a new filter is a new list.
 */
export function mergeFilterChange(
  current: SummariesSearch,
  drawnWith: SummaryFilters,
  next: SummaryFilters,
): SummariesSearch {
  const keys = new Set([...Object.keys(drawnWith), ...Object.keys(next)]) as Set<
    keyof SummaryFilters
  >;
  const merged: Record<string, unknown> = { ...current };
  delete merged.pages;
  delete merged.opened;
  for (const key of keys) {
    if (JSON.stringify(drawnWith[key]) !== JSON.stringify(next[key])) merged[key] = next[key];
  }
  return merged;
}
